import type { RouteLocationRaw } from 'vue-router';
import { useSessionStorage } from '@vueuse/core';
import { computed, readonly } from 'vue';
import * as z from 'zod/v4-mini';
import { useAuth } from '#layers/access/public';
import { storageKey } from '#layers/base/public';
import { settingsOverlayPath } from '#layers/global-settings/public';
import { useWorkspaceStore } from '#layers/workspace/public';

export function onboardingProgressKey(userId: string): string {
	return storageKey(`onboarding-progress:${userId}`);
}

export const onboardingStatuses = ['active', 'dismissed', 'completed'] as const;

export const onboardingSteps = ['workspace', 'data-source', 'connect'] as const;
export type OnboardingStepId = (typeof onboardingSteps)[number];

export const onboardingProgressSchema = z.object({
	status: z.enum(onboardingStatuses),
	currentStep: z.enum(onboardingSteps),
	workspaceSlug: z.string(), // slug of the workspace created during onboarding
});

export type OnboardingProgress = z.infer<typeof onboardingProgressSchema>;

const serializer = {
	read(raw: string): OnboardingProgress | null {
		try {
			const parsed = z.safeParse(onboardingProgressSchema, JSON.parse(raw));
			return parsed.success ? parsed.data : null;
		}
		catch {
			return null;
		}
	},
	write(value: OnboardingProgress | null): string {
		return JSON.stringify(value);
	},
};

/**
 * Used by routing middleware that only needs a one-shot value and must
 * not leave a storage listener behind on every navigation
 */
export function readOnboardingProgress(userId: string): OnboardingProgress | null {
	if (typeof sessionStorage === 'undefined') return null;

	const raw = sessionStorage.getItem(onboardingProgressKey(userId));

	return raw === null ? null : serializer.read(raw);
}

export function useOnboarding() {
	const { currentWorkspaceSlug } = useWorkspaceStore();
	const { currentUser } = useAuth();

	// Authoritative session-backed record, keyed to the current user so it can't
	// bleed across a logout/login in the same tab. `null` until `start()` seeds it.
	const progress = useSessionStorage<OnboardingProgress | null>(
		() => onboardingProgressKey(currentUser.value?.id ?? ''),
		null,
		{ serializer },
	);

	function update(patch: (current: OnboardingProgress) => OnboardingProgress) {
		const current = progress.value;
		if (!current) return;

		const next = patch(current);

		if (next === current) return;

		progress.value = next;
	}

	/**
	 * Enter onboarding at the workspace step. Called when the user lands on the workspace
	 * step so the routing guard pins them to the flow before a workspace exists (the
	 * slug is filled in later by `start()`). No-op once any record exists — in
	 * progress, dismissed, or completed — so a returning user is never re-trapped.
	 */
	function begin() {
		if (progress.value !== null) return;

		progress.value = {
			status: 'active',
			currentStep: 'workspace',
			workspaceSlug: '',
		};
	}

	/**
	 * Create the onboarding record for the given workspace. Called from the workspace step
	 * after the workspace is hydrated. A freshly created workspace is authoritative, so a
	 * stale record from a prior attempt in the same tab is replaced; only a retried
	 * submit of the same workspace is a no-op.
	 */
	function start(workspaceSlug: string) {
		if (progress.value?.workspaceSlug === workspaceSlug) return;

		progress.value = {
			status: 'active',
			currentStep: 'data-source',
			workspaceSlug,
		};
	}

	function setCurrentStep(step: OnboardingStepId) {
		update((current) =>
			current.currentStep === step ? current : { ...current, currentStep: step });
	}

	function dismiss() {
		update((current) =>
			current.status === 'dismissed' ? current : { ...current, status: 'dismissed' });
	}

	function complete() {
		update((current) =>
			current.status === 'completed' ? current : { ...current, status: 'completed' });
	}

	// String path (not a named route) since there may be no current workspace to
	// scope a route object to; `/~/...` resolves the backdrop workspace itself.
	const dashboard: RouteLocationRaw = settingsOverlayPath('organization/workspaces');

	const workspaceHome = computed<RouteLocationRaw>(() =>
		currentWorkspaceSlug.value
			? { name: 'workspace-home', params: { workspace: currentWorkspaceSlug.value } }
			: dashboard,
	);

	function dataSourceView(source: string | null): RouteLocationRaw {
		return currentWorkspaceSlug.value && source
			? { name: 'data-model.source', params: { workspace: currentWorkspaceSlug.value, source } }
			: workspaceHome.value;
	}

	return {
		progress: readonly(progress),
		begin,
		start,
		setCurrentStep,
		dismiss,
		complete,
		dashboard,
		workspaceHome,
		dataSourceView,
	};
}
